import axios from "axios";
import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage } from "./localStorage";
import { can, isClientAdmin, isPlatformAdmin } from "./accessControl";

const listeners = new Set();
let state = { user: null, loading: true, error: "", revision: 0 };
let initializedToken;
let sequence = 0;
let pending;
let deniedRefresh;
const emit = (next) => {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
};
const clear = () => {
  ["tenant_id", "documentList", "currentDoc", "chat_session_id", "session_id"].forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new Event("client-context-changed"));
};
export function normalizeIdentity(raw, preferredClient) {
  if (!raw || typeof raw.is_super_admin !== "boolean") {
    throw new Error("Your account access could not be verified.");
  }
  const memberships = (Array.isArray(raw.memberships) ? raw.memberships : raw.membership ? [raw.membership] : [])
    .filter((m) => m.client_id != null && m.active !== false && !m.deleted)
    .map((m) => ({
      ...m,
      roles: Array.isArray(m.roles) ? m.roles : [],
      role_ids: Array.isArray(m.role_ids) ? m.role_ids : [],
      permissions: Array.isArray(m.permissions) ? m.permissions : [],
      is_client_admin: m.is_client_admin === true,
    }));
  const ids = memberships.map((m) => String(m.client_id));
  // A server-selected context is accepted only when it is an accessible membership.
  const selected = [preferredClient, raw.client_id, memberships[0]?.client_id]
    .find((id) => id != null && (raw.is_super_admin || ids.includes(String(id))));
  const membership = memberships.find((m) => String(m.client_id) === String(selected));
  const isSuperAdmin = isPlatformAdmin(raw);
  const clientAdmin = isClientAdmin({ ...raw, memberships }, selected);
  const assignedRoles = membership?.roles || [];
  return {
    ...raw, memberships, client_id: selected ?? null, tenantId: selected ?? null,
    is_client_admin: clientAdmin && !isSuperAdmin,
    rawRole: isSuperAdmin ? 0 : null,
    role: isSuperAdmin ? "platform_admin" : clientAdmin ? "tenant_admin" : assignedRoles[0]?.role_key || null,
    roleLabel: isSuperAdmin ? "Super Admin" : clientAdmin ? "Client Admin" : assignedRoles.map((role) => role.name).join(", ") || (membership ? "Custom access" : "No access"),
    permissions: isSuperAdmin || clientAdmin ? ["*"] : membership?.permissions || [],
  };
}
async function refresh(preferredClient = state.user?.client_id, revalidate = false) {
  const token = getDataFromLocalStorage("token");
  initializedToken = token;
  const current = ++sequence;
  if (!revalidate) {
    emit({ user: null, loading: true, error: "", revision: state.revision + 1 });
    clear();
  }
  if (!token) {
    emit({ user: null, loading: false });
    return null;
  }
  try {
    const response = await axios.get(`${baseUrl}/users/me`, {
      headers: { Authorization: `Bearer ${token}`, ...(preferredClient != null ? { "X-Client-ID": String(preferredClient) } : {}) },
      authBootstrap: true,
    });
    const user = normalizeIdentity(response.data?.data?.user, preferredClient);
    if (current !== sequence) return null;
    if (revalidate && JSON.stringify(user) !== JSON.stringify(state.user)) {
      clear();
      emit({ user, loading: false, error: "", revision: state.revision + 1 });
    } else if (!revalidate) {
      emit({ user, loading: false });
    }
    return user;
  } catch (error) {
    if (current !== sequence) return null;
    if (error.response?.status === 401) localStorage.removeItem("token");
    emit({ user: null, loading: false, error: "Unable to verify your access. Please sign in again or retry." });
    throw error;
  }
}
export const authStore = {
  subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
  getSnapshot: () => state,
  can: (permission, clientId) => !state.loading && can(state.user, permission, clientId),
  refresh,
  initialize: () => {
    const token = getDataFromLocalStorage("token");
    if (initializedToken === token) return pending;
    pending = refresh().catch(() => null);
    return pending;
  },
  switchClient: async (clientId) => {
    if (!state.user?.is_super_admin && !state.user?.memberships.some((m) => String(m.client_id) === String(clientId))) {
      throw new Error("Client access denied.");
    }
    return refresh(clientId);
  },
  invalidate: (status) => {
    if (status === 401) localStorage.removeItem("token");
    if (!state.user) return;
    if (status === 403) {
      if (!deniedRefresh) {
        deniedRefresh = refresh(state.user.client_id, true).catch(() => null).finally(() => { deniedRefresh = null; });
      }
      return deniedRefresh;
    }
    refresh().catch(() => null);
  },
  logout: () => {
    sequence++;
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    initializedToken = undefined;
    emit({ user: null, loading: false, revision: state.revision + 1 });
    clear();
  },
};
